<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionRoot,
  TransitionChild,
} from '@headlessui/vue'
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
import type { LightbaseUser } from '@/api/users.api'
import { customerDisplayName, statusClass, statusLabel } from '@/utils/customers'
import { useMountedWhileOpen } from '@/composables/useMountedWhileOpen'

const RESULT_LIMIT = 10

const props = defineProps<{
  open: boolean
  users: LightbaseUser[]
  loading?: boolean
}>()

const emit = defineEmits<{
  close: []
  select: [user: LightbaseUser]
}>()

const mounted = useMountedWhileOpen(() => props.open)

const query = ref('')
const activeIndex = ref(0)
const inputRef = ref<HTMLInputElement | null>(null)
const listRef = ref<HTMLUListElement | null>(null)

const results = computed(() => {
  const q = query.value.trim().toLowerCase()
  let list = props.users.slice()

  if (q) {
    list = list.filter((u) => {
      const name = customerDisplayName(u).toLowerCase()
      return name.includes(q) || u.email.toLowerCase().includes(q)
    })
    list.sort((a, b) => matchScore(a, q) - matchScore(b, q))
  } else {
    list.sort((a, b) => customerDisplayName(a).localeCompare(customerDisplayName(b)))
  }

  return list.slice(0, RESULT_LIMIT)
})

function matchScore(user: LightbaseUser, q: string): number {
  const name = customerDisplayName(user).toLowerCase()
  const email = user.email.toLowerCase()
  if (name.startsWith(q)) return 0
  if (email.startsWith(q)) return 1
  if (name.includes(q)) return 2
  return 3
}

watch(
  () => props.open,
  async (open) => {
    if (!open) return
    query.value = ''
    activeIndex.value = 0
    await nextTick()
    inputRef.value?.focus()
  },
)

watch(query, () => {
  activeIndex.value = 0
})

function move(delta: number) {
  const count = results.value.length
  if (!count) return
  activeIndex.value = (activeIndex.value + delta + count) % count
  nextTick(() => {
    const el = listRef.value?.querySelector('[data-active="true"]')
    if (el instanceof HTMLElement) el.scrollIntoView({ block: 'nearest' })
  })
}

function onInputKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    move(1)
    return
  }
  if (event.key === 'ArrowUp') {
    event.preventDefault()
    move(-1)
    return
  }
  if (event.key === 'Enter' && !event.isComposing) {
    event.preventDefault()
    const user = results.value[activeIndex.value]
    if (user) select(user)
  }
}

function select(user: LightbaseUser) {
  emit('select', user)
  emit('close')
}
</script>

<template>
  <TransitionRoot v-if="mounted" appear :show="open" as="template">
    <Dialog class="relative z-[60]" @close="emit('close')">
      <TransitionChild
        enter="transition-opacity duration-200"
        enter-from="pointer-events-none opacity-0"
        enter-to="opacity-100"
        leave="transition-opacity duration-150"
        leave-from="opacity-100"
        leave-to="pointer-events-none opacity-0"
      >
        <div class="fixed inset-0 bg-black/20 dark:bg-black/60" />
      </TransitionChild>

      <div class="pointer-events-none fixed inset-0 overflow-y-auto p-4 pt-[12vh] sm:pt-[18vh]">
        <TransitionChild
          enter="transition duration-200 ease-out"
          enter-from="scale-95 opacity-0"
          enter-to="scale-100 opacity-100"
          leave="transition duration-150 ease-in"
          leave-from="scale-100 opacity-100"
          leave-to="scale-95 opacity-0"
        >
          <DialogPanel
            class="pointer-events-auto mx-auto w-full max-w-lg overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
          >
            <DialogTitle class="sr-only">Search customers</DialogTitle>
            <div class="flex items-center gap-3 border-b border-gray-100 px-4">
              <MagnifyingGlassIcon class="h-5 w-5 shrink-0 text-gray-400" />
              <input
                ref="inputRef"
                v-model="query"
                type="text"
                autocomplete="off"
                autocorrect="off"
                spellcheck="false"
                placeholder="Search by name or email…"
                class="w-full border-0 bg-transparent py-3.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-transparent focus:outline-none focus:ring-0"
                @keydown="onInputKeydown"
              />
            </div>

            <ul
              v-if="results.length"
              ref="listRef"
              class="max-h-80 overflow-y-auto py-1"
              role="listbox"
              aria-label="Customer search results"
            >
              <li
                v-for="(u, i) in results"
                :key="u.id"
                role="option"
                :aria-selected="i === activeIndex"
                :data-active="i === activeIndex ? 'true' : undefined"
                class="flex cursor-pointer items-center justify-between gap-3 px-4 py-2.5"
                :class="i === activeIndex ? 'bg-forest-50' : 'hover:bg-gray-50'"
                @mouseenter="activeIndex = i"
                @click="select(u)"
              >
                <div class="min-w-0">
                  <p class="truncate text-sm font-medium text-gray-900">
                    {{ customerDisplayName(u) }}
                  </p>
                  <p class="truncate font-mono text-xs text-gray-400">{{ u.email }}</p>
                </div>
                <span
                  class="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap"
                  :class="statusClass(u.subscription_status)"
                >
                  {{ statusLabel(u.subscription_status) }}
                </span>
              </li>
            </ul>
            <p v-else-if="loading" class="px-4 py-8 text-center text-sm text-gray-400">
              Loading customers…
            </p>
            <p v-else class="px-4 py-8 text-center text-sm text-gray-400">
              {{ query.trim() ? 'No customers match that search.' : 'No customers to search.' }}
            </p>

            <div
              class="flex items-center gap-3 border-t border-gray-100 px-4 py-2 text-[11px] text-gray-400"
            >
              <span class="inline-flex items-center gap-1">
                <kbd
                  class="rounded border border-gray-200 bg-gray-50 px-1 py-0.5 font-mono text-[10px] text-gray-500"
                >↑</kbd>
                <kbd
                  class="rounded border border-gray-200 bg-gray-50 px-1 py-0.5 font-mono text-[10px] text-gray-500"
                >↓</kbd>
                navigate
              </span>
              <span class="inline-flex items-center gap-1">
                <kbd
                  class="rounded border border-gray-200 bg-gray-50 px-1 py-0.5 font-mono text-[10px] text-gray-500"
                >↵</kbd>
                open
              </span>
              <span class="inline-flex items-center gap-1">
                <kbd
                  class="rounded border border-gray-200 bg-gray-50 px-1 py-0.5 font-mono text-[10px] text-gray-500"
                >esc</kbd>
                close
              </span>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
